/**
 * Template 1: Minimalist Product Reveal
 *
 * Clean, sophisticated reveal animation focusing on the product.
 * Target: Premium/luxury e-commerce brands
 *
 * Two-Phase Animation Timeline (15 seconds @ 60fps = 900 frames):
 *
 * PHASE 1: Product Showcase (0-7.5s / frames 0-225)
 * - 0.0-0.5s (0-15):    Background fade in
 * - 0.5-1.5s (15-45):   Product image reveal with scale
 * - 1.5-2.5s (45-75):   Title typewriter effect
 * - 2.5-3.5s (75-105):  Price reveal with strike-through
 * - 3.5-4.5s (105-135): Rating stars animate in
 * - 4.5-7.0s (135-210): Hold with subtle floating
 * - 7.0-7.5s (210-225): Fade out transition
 *
 * PHASE 2: Caption Scene (7.5-15s / frames 225-450)
 * - 7.5-8.0s (225-240): Scene fade in
 * - 8.0-12.0s (240-360): Caption typewriter
 * - 12.0-15s (360-450): Hold with swipe-up indicator
 *
 * Visual Enhancements:
 * - Subtle sparkles around the product
 * - Shimmer effect over the price
 * - Floating geometric shapes in background
 * - Soft ambient lighting
 */

import React from 'react';
import { AbsoluteFill, useCurrentFrame, useVideoConfig, interpolate } from 'remotion';
import type { MinimalistRevealProps, VisualEffectsConfig } from '../types';
import { DEFAULT_VISUAL_EFFECTS, FONT_ID_TO_FAMILY } from '../types';
import { adjustBrightness } from '../utils';

import { ProductImage } from '../components/ProductImage';
import { AnimatedText } from '../components/AnimatedText';
import { AnimatedPrice } from '../components/AnimatedPrice';
import { StarRating } from '../components/StarRating';
import { CaptionScene } from '../components/CaptionScene';
import { TemplateBackground } from '../components/TemplateBackground';
// Decorative components
import { SparkleCluster } from '../components/Sparkles';
import { ShimmerEffect } from '../components/ShimmerEffect';
import { DiscountBadge } from '../components/DiscountBadge';

export const MinimalistReveal: React.FC<MinimalistRevealProps> = ({
  productTitle,
  productImage,
  originalPrice,
  salePrice,
  brandColor = '#1a1a2e',
  brandLogo,
  rating,  // Optional - no default, hides when undefined/0
  reviewCount,  // Optional - no default, hides when undefined/0
  headlineText,
  discountCode,
  discountLabel,
  captionText,
  captionColor = '#ffffff',
  captionFont = 'Modern',
  effects: effectsOverride,
  backgroundType = 'aurora',
  backgroundPalette = 'aurora',
  textAnimation = 'word_reveal',
  language,
  showPriceDisclaimer = false,
}) => {
  const frame = useCurrentFrame();
  const { fps } = useVideoConfig();

  // Merge user effects with defaults
  const effects: VisualEffectsConfig = {
    ...DEFAULT_VISUAL_EFFECTS,
    ...effectsOverride,
  };

  // Resolve font family from font ID (for Phase 1 text components)
  const fontFamily = FONT_ID_TO_FAMILY[captionFont] || 'Inter, sans-serif';

  // Phase transition point
  const PHASE_TRANSITION = fps * 7.5; // Frame 225

  // Phase 1: Product showcase timeline (compressed to 7.5s)
  const PRODUCT_START = fps * 0.5;      // 0.5s
  const TITLE_START = fps * 1.5;        // 1.5s
  const PRICE_START = fps * 2.5;        // 2.5s
  const RATING_START = fps * 3.5;       // 3.5s
  const LOGO_START = fps * 4.5;         // 4.5s

  // Phase 1 fade out (for transition to Phase 2)
  const phase1Opacity = interpolate(
    frame,
    [PHASE_TRANSITION - fps * 0.5, PHASE_TRANSITION],
    [1, 0],
    { extrapolateLeft: 'clamp', extrapolateRight: 'clamp' }
  );

  // Brand logo opacity
  const logoOpacity = brandLogo
    ? interpolate(frame, [LOGO_START, LOGO_START + fps * 0.5], [0, 1], {
        extrapolateLeft: 'clamp',
        extrapolateRight: 'clamp',
      })
    : 0;

  // Extract accent color for decorative elements
  const accentColor = adjustBrightness(brandColor, 40);
  const sparkleColor = 'rgba(255, 255, 255, 0.9)';

  // Determine if we're in Phase 1 or Phase 2
  const isPhase1 = frame < PHASE_TRANSITION;
  const isPhase2 = frame >= PHASE_TRANSITION - fps * 0.5; // Start fading in early

  // Phase 1 content (shared between both background types)
  const phase1Content = (
    <>
      <AbsoluteFill
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 50,
          opacity: phase1Opacity,
        }}
      >
        {/* Brand Logo (top-left) */}
        {brandLogo && (
          <div
            style={{
              position: 'absolute',
              top: 100,
              left: 40,
              opacity: logoOpacity * 0.7,
            }}
          >
            <img
              src={brandLogo}
              alt="Brand"
              style={{
                height: 40,
                objectFit: 'contain',
              }}
            />
          </div>
        )}

        {/* Headline Text (uses selected text animation) */}
        {headlineText && (
          <div style={{ marginBottom: 50, maxWidth: 800 }}>
            <AnimatedText
              text={headlineText}
              animationType={textAnimation}
              startFrame={fps * 0.2}
              color={captionColor}
              fontSize={48}
              fontWeight={600}
              fontFamily={fontFamily}
              textAlign="center"
              wordDelay={5}
            />
          </div>
        )}

        {/* Product Image with Sparkle Cluster */}
        <div style={{ marginBottom: 40, position: 'relative' }}>
          <ProductImage
            src={productImage}
            alt={productTitle}
            startFrame={PRODUCT_START}
            width={650}
            height={650}
            borderRadius={24}
            shadow="0 30px 80px rgba(0,0,0,0.4)"
            entranceAnimation="fadeScale"
            enableFloat={true}
            floatAmplitude={4}
          />

          {/* Sparkles around the product */}
          {effects.sparkles && (
            <>
              <SparkleCluster
                centerX={50}
                centerY={50}
                radius={45}
                count={16}
                color={sparkleColor}
                startFrame={PRODUCT_START + fps * 0.8}
                seed={11111}
              />

              {/* Additional sparkle cluster top-right */}
              <SparkleCluster
                centerX={85}
                centerY={15}
                radius={12}
                count={8}
                color="#FFD700"
                startFrame={PRODUCT_START + fps * 1.2}
                seed={22222}
              />
            </>
          )}
        </div>

        {/* Product Title (uses selected text animation) */}
        <div style={{ marginBottom: 24, maxWidth: 700 }}>
          <AnimatedText
            text={productTitle}
            animationType={textAnimation}
            startFrame={TITLE_START}
            color={captionColor}
            fontSize={52}
            fontWeight={700}
            fontFamily={fontFamily}
            textAlign="center"
            wordDelay={5}
          />
        </div>

        {/* Price Display with Shimmer */}
        <div style={{ marginBottom: 24, position: 'relative' }}>
          <AnimatedPrice
            originalPrice={originalPrice}
            salePrice={salePrice}
            startFrame={PRICE_START}
            salePriceColor={captionColor}
            originalPriceColor="rgba(255,255,255,0.5)"
            salePriceFontSize={64}
            originalPriceFontSize={36}
            direction="row"
            gap={24}
            showPriceDisclaimer={showPriceDisclaimer}
            language={language}
          />

          {/* Shimmer effect over the price */}
          {effects.priceShimmer && (
            <div
              style={{
                position: 'absolute',
                top: 0,
                left: -20,
                right: -20,
                bottom: 0,
                overflow: 'hidden',
                pointerEvents: 'none',
              }}
            >
              <ShimmerEffect
                width={100}
                color="rgba(255, 255, 255, 0.35)"
                delay={fps * 0.8}
                duration={35}
                repeatCount={2}
                repeatGap={fps * 2}
                angle={-20}
                intensity={0.7}
                startFrame={PRICE_START}
                blur={12}
              />
            </div>
          )}
        </div>

        {/* Star Rating with sparkle accent
            Quality threshold: Only show if rating >= 4.0 AND reviewCount >= 5
            This prevents displaying inflated or statistically insignificant ratings */}
        {rating !== undefined && rating >= 4.0 && reviewCount !== undefined && reviewCount >= 5 && (
          <div style={{ position: 'relative' }}>
            <StarRating
              rating={rating}
              reviewCount={reviewCount}
              startFrame={RATING_START}
              filledColor="#FFD700"
              emptyColor="rgba(255,255,255,0.15)"
              textColor="#ffffff"
              starSize={32}
              animationDurationPerStar={0.1}
            />

            {/* Tiny sparkles near the stars */}
            {effects.sparkles && (
              <SparkleCluster
                centerX={50}
                centerY={50}
                radius={30}
                count={6}
                color="#FFD700"
                startFrame={RATING_START + fps * 0.5}
                seed={33333}
              />
            )}
          </div>
        )}
        {/* Discount Badge */}
        <DiscountBadge
          discountCode={discountCode}
          discountLabel={discountLabel}
          variant="minimal"
          showFrom={fps * 4.8}
          fontFamily={fontFamily}
          textAnimation={textAnimation}
        />
      </AbsoluteFill>
    </>
  );

  // Phase 2 content (caption overlay - no background, transparent)
  const phase2Content = captionText ? (
    <CaptionScene
      captionText={captionText}
      captionColor={captionColor}
      captionFont={captionFont}
      textAnimation={textAnimation}
      startFrame={PHASE_TRANSITION}
      gradientPreset="none"
      showSwipeUp={true}
      language={language}
    />
  ) : null;

  // Combined content for both phases (layered on top of background)
  const combinedContent = (
    <>
      {/* Phase 1 content with fade out */}
      {isPhase1 && phase1Content}
      {/* Phase 2 content with fade in */}
      {isPhase2 && phase2Content}
    </>
  );

  return (
    <TemplateBackground
      backgroundType={backgroundType}
      backgroundPalette={backgroundPalette}
      colorTheme={{
        brandColor,
        accentColor,
      }}
      effects={effects}
      fps={fps}
      rippleConfig={{ startFrame: PRODUCT_START }}
    >
      {combinedContent}
    </TemplateBackground>
  );
};
